/**
 * Shared assumption defaults (Settings → Home buyer / Investor):
 * `GET/PUT /api/me/deal-model-settings`, contract deal-model-user-settings-v2.
 * Exact decimal strings travel through lossless JSON; the request is checked
 * against the descriptors and the frozen UTF-8 ceiling before it is sent.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './http'
import {
  assertJsonUtf8Ceiling, assertSettingsRevision, DEAL_MODEL_RESOURCE_LIMITS, parseDealModelUserSettings, validateUserSettingsRequestValues,
} from './dealModelContract'
import type { DealModelValueDescriptor } from './dealModelTypes'

export const userSettingsKey = ['dealModelUserSettings'] as const

export function useDealModelUserSettings(enabled = true) {
  return useQuery({
    queryKey: userSettingsKey,
    queryFn: async ({ signal }) => parseDealModelUserSettings(await api('dealModelUserSettings', { signal, lossless: true })),
    enabled,
    retry: false,
  })
}

export function usePutDealModelUserSettings() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ expectedRevision, defaultAssumptions, descriptors }: { expectedRevision: string; defaultAssumptions: Record<string, string | boolean | null>; descriptors: DealModelValueDescriptor[] }) => {
      const body = {
        expectedRevision: assertSettingsRevision(expectedRevision, 'expectedRevision'),
        defaultAssumptions: validateUserSettingsRequestValues(defaultAssumptions, descriptors, 'defaultAssumptions'),
      }
      assertJsonUtf8Ceiling(body, 'PUT /api/me/deal-model-settings request body', DEAL_MODEL_RESOURCE_LIMITS.evaluationInputUtf8Bytes)
      return parseDealModelUserSettings(await api('putDealModelUserSettings', { body, lossless: true }))
    },
    onSuccess: async (updated) => {
      client.setQueryData(userSettingsKey, updated)
      await Promise.all([
        client.invalidateQueries({ queryKey: ['dealsV2'] }),
        client.invalidateQueries({ queryKey: ['calculationCards'] }),
      ])
    },
  })
}
